import cn from 'classnames';
import { useSelector } from 'react-redux';
import { isMobile } from 'components/Shared/responsive';
import { useToggle } from 'hooks';
import { IconArrow } from 'components/Shared/Icon';
import styles from './accordion.scss';
import { getGetImageUrl } from 'services/ApiKnowledgebaseService';
import { Reducer, useEffect, useReducer } from 'react';

interface AccordionProps {
  text01: string;
  image01: string;
  link01url: string;
  link01text: string;
  text02: string;
  image02: string;
  link02url: string;
  link02text: string;
  text03: string;
  image03: string;
  link03url: string;
  link03text: string;
  text04: string;
  image04: string;
  link04url: string;
  link04text: string;
  text05: string;
  image05: string;
  link05url: string;
  link05text: string;
  numbered: string;
}

export const Accordion = ({
  text01,
  image01,
  link01url,
  link01text,
  text02,
  image02,
  link02url,
  link02text,
  text03,
  image03,
  link03url,
  link03text,
  text04,
  image04,
  link04url,
  link04text,
  text05,
  image05,
  link05url,
  link05text,
  numbered
}: AccordionProps) => {
  const items: ItemProps[] = [
    { text: text01, images: image01.split(','), linkUrl: link01url, linkText: link01text },
    { text: text02, images: image02.split(','), linkUrl: link02url, linkText: link02text },
    { text: text03, images: image03.split(','), linkUrl: link03url, linkText: link03text },
    { text: text04, images: image04.split(','), linkUrl: link04url, linkText: link04text },
    { text: text05, images: image05.split(','), linkUrl: link05url, linkText: link05text }
  ];
  return (
    <div className={styles.accordion}>
      {items
        .filter(item => item.text !== '')
        .map((item, i) => (
          <Item key={i} numbered={numbered === '1'} number={`${i + 1}`} {...item} />
        ))}
    </div>
  );
};

interface ItemProps {
  text: string;
  images: string[];
  linkUrl: string;
  linkText: string;
  numbered?: boolean;
  number?: string;
}

interface ItemState {
  images: string[];
}

const initialItemState: ItemState = {
  images: []
};

type ItemAction = { type: 'image'; value: string };

const itemReducer: Reducer<ItemState, ItemAction> = (state, action) => {
  switch (action.type) {
    case 'image': {
      return { ...state, images: [...state.images, action.value] };
    }
    default:
      return state;
  }
};

const Item = ({ text, images, linkUrl, linkText, numbered, number }: ItemProps) => {
  const [state, dispatch] = useReducer(itemReducer, initialItemState);
  const mobile = useSelector(isMobile);
  const [isOpen, toggleOpen] = useToggle(false);
  const [first, second, ...rest] = text.split(' ');
  const restText = rest.join(' ');
  const hasImages = images.length > 0;

  useEffect(() => {
    images.forEach(img => {
      void getGetImageUrl(img).then(response => {
        dispatch({ type: 'image', value: response.url });
      });
    });
  }, [images]);

  return (
    <div
      className={cn(styles.item, {
        [styles.open]: isOpen || !mobile
      })}
      onClick={mobile ? toggleOpen : undefined}
    >
      <div
        className={cn(styles.marker, {
          [styles.numbered]: numbered
        })}
      >
        {numbered && number}
      </div>
      <div className={styles.text}>
        <div>
          <span className={styles.first}>
            {first} {second}
          </span>
          &nbsp;
          <span>{restText}</span>
        </div>
        {linkUrl && linkText && (isOpen || !mobile) && (
          <a href={linkUrl} className={styles.link}>
            {linkText}
          </a>
        )}
        {hasImages && (isOpen || !mobile) && (
          <div className={styles.images}>
            {state.images.map(image => (
              <div
                key={image}
                style={{ backgroundImage: `url(${image})` }}
                className={styles.image}
              />
            ))}
          </div>
        )}
      </div>
      {mobile && (
        <IconArrow appearance={isOpen ? 'flip-270' : 'flip-90'} className={styles.arrow} />
      )}
    </div>
  );
};
